import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getVideos,
  assignVideoToUser,
  createVideo,
  updateVideo,
  deleteVideo,
  getVideoAssignments,
  getCoachingAnalytics
} from '../../services/coachingAPI';
import { getUsers } from '../../services/observationAPI';
import {
  PlayCircle,
  Users,
  AlertTriangle,
  PlusCircle,
  XCircle,
  ListChecks,
  CheckCircle,
  Loader2,
  Info,
  Edit2,
  Trash2,
  BarChart3,
  Calendar,
  Star,
  Filter
} from 'lucide-react';
import { Button, Input, Select, Modal, Form, Card, Tag, DatePicker } from 'antd';
import toast from 'react-hot-toast';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const CoachingLibrary = () => {
  const { user } = useSelector((state) => state.profile);
  const [videos, setVideos] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    difficulty: 'all'
  });

  // Modal states
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [assignVideoId, setAssignVideoId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [videoAssignments, setVideoAssignments] = useState([]);
  const [detailedVideoInfo, setDetailedVideoInfo] = useState({ videoId: null, videoTitle: '' });
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  const [form] = Form.useForm();
  const videoModalRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [videosData, usersData, analyticsData] = await Promise.all([
        getVideos(),
        user.accountType === 'Admin' || user.accountType === 'SuperAdmin' ? getUsers() : Promise.resolve([]),
        user.accountType === 'Admin' || user.accountType === 'SuperAdmin' ? getCoachingAnalytics() : Promise.resolve(null)
      ]);

      setVideos(videosData || []);
      setUsers(usersData || []);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const params = {};
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.difficulty !== 'all') params.difficulty = filters.difficulty;

      let videosData = await getVideos(params);
      
      if (filters.search) {
        videosData = videosData.filter(video =>
          video.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          video.description?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setVideos(videosData || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    }
  };

  const handleCreateVideo = async (values) => {
    try {
      const createdVideo = await createVideo(values);
      setVideos(prev => [createdVideo, ...prev]);
      setIsVideoModalOpen(false);
      form.resetFields();
      toast.success('Video created successfully');
    } catch (error) {
      console.error('Error creating video:', error);
      toast.error(error.response?.data?.message || 'Failed to create video');
    }
  };

  const handleUpdateVideo = async (values) => {
    try {
      const updatedVideo = await updateVideo(editingVideo._id, values);
      setVideos(prev => prev.map(v => v._id === updatedVideo._id ? updatedVideo : v));
      setIsVideoModalOpen(false);
      form.resetFields();
      setEditingVideo(null);
      toast.success('Video updated successfully');
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error(error.response?.data?.message || 'Failed to update video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    Modal.confirm({
      title: 'Delete Video',
      content: 'Are you sure you want to delete this video? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteVideo(videoId);
          setVideos(prev => prev.filter(v => v._id !== videoId));
          toast.success('Video deleted successfully');
        } catch (error) {
          console.error('Error deleting video:', error);
          toast.error(error.response?.data?.message || 'Failed to delete video');
        }
      },
    });
  };

  const handleAssignVideo = async (assignmentData) => {
    try {
      await assignVideoToUser(assignVideoId, selectedUserId, assignmentData);
      toast.success('Video assigned successfully');
      setIsAssignModalOpen(false);
      setSelectedUserId(null);
      setAssignVideoId(null);
      
      // Refresh assignments if details modal is open
      if (detailedVideoInfo.videoId === assignVideoId && isDetailsModalOpen) {
        fetchVideoAssignmentsForModal(assignVideoId, detailedVideoInfo.videoTitle);
      }
    } catch (error) {
      console.error('Error assigning video:', error);
      toast.error(error.response?.data?.message || 'Failed to assign video');
    }
  };

  const fetchVideoAssignmentsForModal = async (videoId, videoTitle) => {
    setIsLoadingAssignments(true);
    setDetailedVideoInfo({ videoId, videoTitle });
    try {
      const assignmentsData = await getVideoAssignments(videoId);
      setVideoAssignments(assignmentsData || []);
    } catch (error) {
      console.error('Error fetching video assignments:', error);
      setVideoAssignments([]);
      toast.error('Failed to load assignment details');
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const handleShowDetailsClick = (video) => {
    fetchVideoAssignmentsForModal(video._id, video.title);
    setIsDetailsModalOpen(true);
  };

  const getYoutubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2] && match[2].length === 11 ? match[2] : null;
  };

  const handleVideoClick = (url) => {
    if (getYoutubeVideoId(url)) {
      setSelectedVideoUrl(url);
    } else {
      toast.error("Cannot play this video. The URL might be invalid or not a YouTube link.");
    }
  };

  const renderVideoThumbnail = (video) => {
    const videoId = getYoutubeVideoId(video.url);
    const placeholderImage = 'https://placehold.co/600x400/e2e8f0/94a3b8?text=Video';
    const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : placeholderImage;

    return (
      <div
        className="relative cursor-pointer group aspect-video bg-gray-200"
        onClick={() => handleVideoClick(video.url)}
      >
        <img
          src={thumbnailUrl}
          alt={video.title || "Video Thumbnail"}
          className="w-full h-full object-cover rounded-t-lg transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { 
            e.target.onerror = null;
            e.target.src = placeholderImage; 
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 group-hover:bg-opacity-20 transition-opacity duration-300 rounded-t-lg">
          <PlayCircle className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 transform transition-transform duration-300 group-hover:scale-110" />
        </div>
        {video.duration && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
            {video.duration}m
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <Loader2 className="animate-spin h-12 w-12 text-blue-600" />
        <p className="ml-3 text-lg text-gray-700">Loading Coaching Library...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Coaching Library</h1>
            <p className="text-gray-600 mt-2">Manage and assign safety training videos</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {(user.accountType === 'Admin' || user.accountType === 'SuperAdmin') && (
              <>
                <Button
                  type="default"
                  icon={<BarChart3 size={18} />}
                  onClick={() => setIsAnalyticsModalOpen(true)}
                >
                  Analytics
                </Button>
                <Button
                  type="primary"
                  icon={<PlusCircle size={18} />}
                  onClick={() => {
                    setEditingVideo(null);
                    setIsVideoModalOpen(true);
                    form.resetFields();
                  }}
                >
                  New Video
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="shadow-sm">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <Search
                placeholder="Search videos..."
                onSearch={(value) => setFilters(prev => ({ ...prev, search: value }))}
                className="max-w-md"
                enterButton
              />
              
              <Select
                value={filters.category}
                onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                className="w-48"
              >
                <Option value="all">All Categories</Option>
                <Option value="safety-training">Safety Training</Option>
                <Option value="equipment-operation">Equipment Operation</Option>
                <Option value="emergency-procedures">Emergency Procedures</Option>
                <Option value="compliance">Compliance</Option>
                <Option value="general">General</Option>
              </Select>

              <Select
                value={filters.difficulty}
                onChange={(value) => setFilters(prev => ({ ...prev, difficulty: value }))}
                className="w-40"
              >
                <Option value="all">All Levels</Option>
                <Option value="beginner">Beginner</Option>
                <Option value="intermediate">Intermediate</Option>
                <Option value="advanced">Advanced</Option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Videos Grid */}
        <AnimatePresence>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video) => (
              <motion.div
                key={video._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {renderVideoThumbnail(video)}
                
                <div className="p-2">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg text-gray-800 line-clamp-2" title={video.title}>
                      {video.title}
                    </h3>
                    {(user.accountType === 'Admin' || user.accountType === 'SuperAdmin') && (
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => {
                            setEditingVideo(video);
                            setIsVideoModalOpen(true);
                            form.setFieldsValue(video);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video._id)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2" title={video.description}>
                    {video.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    <Tag color="blue">{video.category?.replace('-', ' ') || 'general'}</Tag>
                    <Tag color={
                      video.difficulty === 'beginner' ? 'green' :
                      video.difficulty === 'intermediate' ? 'orange' : 'red'
                    }>
                      {video.difficulty || 'beginner'}
                    </Tag>
                  </div>

                  {(user.accountType === 'Admin' || user.accountType === 'SuperAdmin') && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        size="small"
                        type="primary"
                        icon={<Users size={14} />}
                        onClick={() => {
                          setAssignVideoId(video._id);
                          setIsAssignModalOpen(true);
                        }}
                        className="flex-1"
                      >
                        Assign
                      </Button>
                      <Button
                        size="small"
                        icon={<ListChecks size={14} />}
                        onClick={() => handleShowDetailsClick(video)}
                        className="flex-1"
                      >
                        Details
                      </Button>
                    </div>
                  )}

                  
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>

        {/* Empty State */}
        {videos.length === 0 && (
          <div className="text-center py-12">
            <Info size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No videos found</h3>
            <p className="text-gray-500">
              {filters.search || filters.category !== 'all' || filters.difficulty !== 'all'
                ? 'No videos match your current filters.'
                : user.accountType === 'Admin' ? "Click 'New Video' to add content to the library." : "There are currently no coaching videos in the library."
              }
            </p>
          </div>
        )}

        {/* Video Player Modal */}
        {selectedVideoUrl && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-md flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedVideoUrl(null)}
          >
            <motion.div
              ref={videoModalRef}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="relative w-full max-w-4xl aspect-video bg-black rounded-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(selectedVideoUrl)}?autoplay=1&rel=0`}
                title="YouTube Video Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              <button
                onClick={() => setSelectedVideoUrl(null)}
                className="absolute top-2 right-2 bg-black bg-opacity-40 text-white rounded-full p-1.5 hover:bg-opacity-70 transition-colors z-10"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </motion.div>
          </div>
        )}

        {/* Video Form Modal */}
        <Modal
          title={editingVideo ? 'Edit Video' : 'Create New Video'}
          open={isVideoModalOpen}
          onCancel={() => {
            setIsVideoModalOpen(false);
            form.resetFields();
            setEditingVideo(null);
          }}
          footer={null}
          width={600}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={editingVideo ? handleUpdateVideo : handleCreateVideo}
            className="space-y-4"
          >
            <Form.Item
              label="Video Title"
              name="title"
              rules={[{ required: true, message: 'Please enter video title' }]}
            >
              <Input placeholder="Enter video title" />
            </Form.Item>

            <Form.Item
              label="YouTube URL"
              name="url"
              rules={[
                { required: true, message: 'Please enter YouTube URL' },
                { pattern: /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.be)/, message: 'Please enter a valid YouTube URL' }
              ]}
            >
              <Input placeholder="https://www.youtube.com/watch?v=..." />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
            >
              <TextArea rows={3} placeholder="Enter video description" />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select placeholder="Select category">
                  <Option value="safety-training">Safety Training</Option>
                  <Option value="equipment-operation">Equipment Operation</Option>
                  <Option value="emergency-procedures">Emergency Procedures</Option>
                  <Option value="compliance">Compliance</Option>
                  <Option value="general">General</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Difficulty"
                name="difficulty"
                rules={[{ required: true, message: 'Please select difficulty' }]}
              >
                <Select placeholder="Select difficulty">
                  <Option value="beginner">Beginner</Option>
                  <Option value="intermediate">Intermediate</Option>
                  <Option value="advanced">Advanced</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label="Duration (minutes)"
              name="duration"
            >
              <Input type="number" placeholder="Enter video duration" />
            </Form.Item>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                onClick={() => {
                  setIsVideoModalOpen(false);
                  form.resetFields();
                  setEditingVideo(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingVideo ? 'Update Video' : 'Create Video'}
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Assign Video Modal */}
        <Modal
          title="Assign Video"
          open={isAssignModalOpen}
          onCancel={() => {
            setIsAssignModalOpen(false);
            setSelectedUserId(null);
            setAssignVideoId(null);
          }}
          footer={null}
          width={500}
          destroyOnClose
        >
          <Form
            layout="vertical"
            onFinish={handleAssignVideo}
            className="space-y-4"
          >
            <Form.Item
              label="Select User"
              name="employeeId"
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select
                placeholder="Select user to assign video"
                showSearch
                optionFilterProp="children"
                onChange={setSelectedUserId}
              >
                {users.map(user => (
                  <Option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} - {user.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Due Date"
              name="dueDate"
            >
              <DatePicker className="w-full" />
            </Form.Item>

            <Form.Item
              label="Notes"
              name="notes"
            >
              <TextArea rows={3} placeholder="Add any notes for the assignment" />
            </Form.Item>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setSelectedUserId(null);
                  setAssignVideoId(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Assign Video
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Video Details Modal */}
        <Modal
          title={`Assignments for: ${detailedVideoInfo.videoTitle}`}
          open={isDetailsModalOpen}
          onCancel={() => setIsDetailsModalOpen(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          {isLoadingAssignments ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
              <p className="ml-3 text-gray-600">Loading assignment details...</p>
            </div>
          ) : videoAssignments.length === 0 ? (
            <div className="text-center py-8">
              <Info size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">No users are currently assigned to this video.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {videoAssignments.map((assignment) => (
                    <tr key={assignment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.employee?.firstName} {assignment.employee?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.employee?.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Tag color={
                          assignment.status === 'completed' ? 'green' :
                          assignment.status === 'in-progress' ? 'blue' : 'orange'
                        }>
                          {assignment.status}
                        </Tag>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${assignment.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{assignment.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(assignment.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>

        {/* Analytics Modal */}
        <Modal
          title="Coaching Analytics"
          open={isAnalyticsModalOpen}
          onCancel={() => setIsAnalyticsModalOpen(false)}
          footer={null}
          width={800}
          destroyOnClose
        >
          {analytics && (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{analytics.overview.totalVideos}</div>
                  <div className="text-sm text-gray-600">Total Videos</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{analytics.overview.totalAssignments}</div>
                  <div className="text-sm text-gray-600">Assignments</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">{analytics.overview.completedAssignments}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-yellow-600">{analytics.overview.completionRate}%</div>
                  <div className="text-sm text-gray-600">Completion Rate</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{analytics.overview.overdue}</div>
                  <div className="text-sm text-gray-600">Overdue</div>
                </div>
              </div>

              {/* Top Videos */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Top Performing Videos</h3>
                <div className="space-y-3">
                  {analytics.topVideos.map((video, index) => (
                    <div key={video._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{video.title}</div>
                        <div className="text-sm text-gray-500">
                          {video.totalAssignments} assignments • {video.completed} completed
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">{Math.round(video.completionRate)}%</div>
                        {video.averageRating && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <Star size={12} className="mr-1" />
                            {video.averageRating}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default CoachingLibrary;